import { NotificationEndpoint } from '../../../model/notification-endpoint';
import { otsBaseApi } from '../../ots-api';
import { removeUndefined } from '../../../util/util';
import {
  ManagedContainerStatusNotificationOptions,
  ManagedContainerStatusNotificationOptionsSave,
  ManagedContainerStatusNotificationSubscription,
  ManagedContainerStatusNotificationSubscriptionSave,
} from '../../../model/managed-container-status-notification';

const base = '/container-status-report-options';
const api = otsBaseApi.enhanceEndpoints({
  addTagTypes: [
    'ManagedContainerStatusOptions',
    'ManagedContainerStatusSubscriptions',
    'NotificationEndpoints',
    'UserNotificationSubscriptions',
    'ObjectObjectChangeLogs',
  ],
});
const optionTags = [
  { type: 'ManagedContainerStatusOptions' as const, id: 'LIST' },
];
const recipientTags = (optionsId: string) => [
  { type: 'ManagedContainerStatusSubscriptions' as const, id: optionsId },
  // A provisioning change also changes the user's virtual preferences listing.
  { type: 'UserNotificationSubscriptions' as const },
];

export const managedContainerStatusNotificationApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getManagedContainerStatusNotificationOptions: builder.query<
      ManagedContainerStatusNotificationOptions[],
      string | undefined
    >({
      query: (regionId) => ({
        url: `${base}/all`,
        params: removeUndefined({ regionId }),
      }),
      providesTags: optionTags,
    }),
    createManagedContainerStatusNotificationOptions: builder.mutation<
      ManagedContainerStatusNotificationOptions,
      ManagedContainerStatusNotificationOptionsSave
    >({
      query: (body) => ({ url: base, method: 'POST', body }),
      invalidatesTags: optionTags,
    }),
    updateManagedContainerStatusNotificationOptions: builder.mutation<
      ManagedContainerStatusNotificationOptions,
      ManagedContainerStatusNotificationOptionsSave & { id: string }
    >({
      query: (body) => ({ url: base, method: 'PUT', body }),
      invalidatesTags: (_result, _error, { id }) => [
        ...optionTags,
        { type: 'ObjectObjectChangeLogs', id },
        { type: 'UserNotificationSubscriptions' },
      ],
    }),
    deleteManagedContainerStatusNotificationOptions: builder.mutation<
      void,
      string
    >({
      query: (id) => ({
        url: `${base}/${encodeURIComponent(id)}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        ...optionTags,
        ...recipientTags(id),
      ],
    }),
    getManagedContainerStatusNotificationSubscriptions: builder.query<
      ManagedContainerStatusNotificationSubscription[],
      string
    >({
      query: (id) => `${base}/${encodeURIComponent(id)}/subscriptions`,
      providesTags: (_result, _error, id) => [
        { type: 'ManagedContainerStatusSubscriptions', id },
      ],
    }),
    createManagedContainerStatusNotificationSubscription: builder.mutation<
      ManagedContainerStatusNotificationSubscription,
      {
        optionsId: string;
        subscription: ManagedContainerStatusNotificationSubscriptionSave;
      }
    >({
      query: ({ optionsId, subscription }) => ({
        url: `${base}/${encodeURIComponent(optionsId)}/subscriptions`,
        method: 'POST',
        body: subscription,
      }),
      invalidatesTags: (_result, _error, { optionsId }) =>
        recipientTags(optionsId),
    }),
    updateManagedContainerStatusNotificationSubscription: builder.mutation<
      ManagedContainerStatusNotificationSubscription,
      {
        optionsId: string;
        id: string;
        subscription: ManagedContainerStatusNotificationSubscriptionSave;
      }
    >({
      query: ({ optionsId, id, subscription }) => ({
        url: `${base}/${encodeURIComponent(
          optionsId
        )}/subscriptions/${encodeURIComponent(id)}`,
        method: 'PUT',
        body: subscription,
      }),
      invalidatesTags: (_result, _error, { optionsId, id }) => [
        ...recipientTags(optionsId),
        { type: 'ObjectObjectChangeLogs', id },
      ],
    }),
    deleteManagedContainerStatusNotificationSubscription: builder.mutation<
      void,
      { optionsId: string; id: string }
    >({
      query: ({ optionsId, id }) => ({
        url: `${base}/${encodeURIComponent(
          optionsId
        )}/subscriptions/${encodeURIComponent(id)}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { optionsId }) =>
        recipientTags(optionsId),
    }),
    getManagedReportRecipientEndpoints: builder.query<
      NotificationEndpoint[],
      string
    >({
      query: (optionsId) =>
        `${base}/${encodeURIComponent(optionsId)}/recipient-endpoints`,
      providesTags: [{ type: 'NotificationEndpoints', id: 'LIST' }],
    }),
    createManagedReportRecipientEndpoint: builder.mutation<
      NotificationEndpoint,
      {
        optionsId: string;
        type: 'EMAIL' | 'SMS';
        value: string;
      }
    >({
      query: ({ optionsId, ...body }) => ({
        url: `${base}/${encodeURIComponent(optionsId)}/recipient-endpoints`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'NotificationEndpoints', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetManagedContainerStatusNotificationOptionsQuery,
  useCreateManagedContainerStatusNotificationOptionsMutation,
  useUpdateManagedContainerStatusNotificationOptionsMutation,
  useDeleteManagedContainerStatusNotificationOptionsMutation,
  useGetManagedContainerStatusNotificationSubscriptionsQuery,
  useCreateManagedContainerStatusNotificationSubscriptionMutation,
  useUpdateManagedContainerStatusNotificationSubscriptionMutation,
  useDeleteManagedContainerStatusNotificationSubscriptionMutation,
  useGetManagedReportRecipientEndpointsQuery,
  useCreateManagedReportRecipientEndpointMutation,
} = managedContainerStatusNotificationApi;
